import { ReactNode, RefObject, useEffect, useId, useRef } from "react";
import styled, { css } from "styled-components";
import { useFocusTrap } from "../../hooks/useFocusTrap";
import { designSystemVars as vars } from "../tokens";
import { IconButton } from "./Button";

type DialogKind = "modal" | "drawer";
export type DialogSize =
  | "content"
  | "aiContext"
  | "xs"
  | "sm"
  | "compactWide"
  | "md"
  | "form"
  | "task"
  | "lg"
  | "xl"
  | "2xl"
  | "3xl";
export type ModalIntent =
  | "standard"
  | "aiContext"
  | "action"
  | "compactAction"
  | "form"
  | "confirmation"
  | "selection"
  | "generation"
  | "editor"
  | "upload"
  | "comparison";
export type ModalHeight = "content" | "short" | "upload" | "medium" | "tall";
type DrawerSide = "left" | "right";
type DrawerSize = "sm" | "md" | "lg";
export type DrawerVariant = "viewport" | "workspace";

interface DialogBaseProps {
  open: boolean;
  title: ReactNode;
  children: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  closeLabel?: string;
  closeOnEscape?: boolean;
  closeOnOverlayClick?: boolean;
  showCloseButton?: boolean;
  descriptionId?: string;
  titleLeading?: ReactNode;
  headerActions?: ReactNode;
  keepMounted?: boolean;
  showHeader?: boolean;
  className?: string;
  surfaceRef?: RefObject<HTMLDivElement | null>;
}

export interface ModalProps extends DialogBaseProps {
  onEscape?: () => void;
  size?: DialogSize;
  intent?: ModalIntent;
  height?: ModalHeight;
}

export interface DrawerProps extends DialogBaseProps {
  onEscape?: () => void;
  escapeFromOutside?: boolean;
  side?: DrawerSide;
  size?: DrawerSize;
  variant?: DrawerVariant;
  modal?: boolean;
}

const modalWidths = {
  content: "max-content",
  aiContext: vars.layout.aiContextModalWidth,
  xs: "468px",
  sm: "500px",
  compactWide: "540px",
  md: "560px",
  form: "661px",
  task: vars.layout.taskModalWidth,
  lg: "675px",
  xl: "707px",
  "2xl": "900px",
  "3xl": "970px",
};

const drawerWidths = {
  content: vars.layout.drawerWidth,
  aiContext: vars.layout.drawerWidth,
  xs: "320px",
  sm: "320px",
  compactWide: vars.layout.drawerWidth,
  md: vars.layout.drawerWidth,
  form: "520px",
  task: vars.layout.drawerWidth,
  lg: "520px",
  xl: "520px",
  "2xl": "520px",
  "3xl": "520px",
};

const modalHeights: Record<ModalHeight, string> = {
  content: "fit-content",
  short: "300px",
  upload: "306px",
  medium: "350px",
  tall: "800px",
};

const isActionIntent = (intent: ModalIntent) =>
  intent === "action" ||
  intent === "compactAction" ||
  intent === "form" ||
  intent === "selection" ||
  intent === "generation";

const isTaskIntent = (intent: ModalIntent) =>
  intent === "selection" || intent === "generation";

const comparisonTopInset = `calc(${vars.layout.topNavigationHeight} + ${vars.spacing.md})`;

const DialogOverlay = styled.div<{
  $modal: boolean;
  $kind: DialogKind;
  $side: DrawerSide;
  $intent: ModalIntent;
  $drawerVariant: DrawerVariant;
}>`
  position: fixed;
  inset: ${({ $kind, $drawerVariant }) =>
    $kind === "drawer" && $drawerVariant === "workspace"
      ? `${vars.layout.topNavigationHeight} 0 0`
      : "0"};
  z-index: ${vars.zIndex.overlay};
  display: flex;
  align-items: ${({ $kind, $intent }) =>
    $kind === "modal"
      ? $intent === "comparison"
        ? "flex-start"
        : "center"
      : "stretch"};
  justify-content: ${({ $kind, $side }) =>
    $kind === "modal"
      ? "center"
      : $side === "left"
        ? "flex-start"
        : "flex-end"};
  padding: ${({ $kind, $intent }) =>
    $kind === "drawer"
      ? "0"
      : $intent === "comparison"
        ? `${comparisonTopInset} ${vars.spacing.lg} ${vars.spacing.lg}`
        : isActionIntent($intent) || $intent === "aiContext"
          ? `56px ${vars.spacing.lg}`
          : vars.spacing.lg};
  background: ${({ $intent }) =>
    $intent === "standard" || $intent === "generation"
      ? vars.color.background.overlay
      : vars.color.background.overlayStrong};
  overflow-y: auto;
  ${({ $modal }) =>
    !$modal &&
    css`
      pointer-events: none;
      background: transparent;
    `}
`;

const DialogSurface = styled.div<{
  $kind: DialogKind;
  $size: DialogSize;
  $side: DrawerSide;
  $intent: ModalIntent;
  $height: ModalHeight;
  $drawerVariant: DrawerVariant;
}>`
  box-sizing: border-box;
  position: relative;
  z-index: ${vars.zIndex.dialog};
  display: flex;
  width: ${({ $kind, $size }: { $kind: DialogKind; $size: DialogSize }) =>
    $kind === "modal" ? modalWidths[$size] : drawerWidths[$size]};
  max-width: ${({ $kind }) =>
    $kind === "modal" ? "calc(100vw - 32px)" : "100vw"};
  max-height: ${({ $kind, $intent }) =>
    $kind === "modal"
      ? $intent === "comparison"
        ? `calc(100vh - ${vars.layout.topNavigationHeight} - ${vars.spacing.xl})`
        : $intent === "aiContext"
          ? "calc(100dvh - 112px)"
          : "calc(100vh - 32px)"
      : "100vh"};
  min-height: ${({ $kind, $intent }) =>
    $kind === "drawer"
      ? "0"
      : $intent === "action"
        ? "250px"
        : $intent === "compactAction"
          ? "200px"
          : $intent === "form"
            ? "586px"
            : "0"};
  height: ${({
    $kind,
    $intent,
    $height,
  }: {
    $kind: DialogKind;
    $intent: ModalIntent;
    $height: ModalHeight;
  }) =>
    $kind === "drawer"
      ? "100%"
      : $intent === "compactAction"
        ? "200px"
        : $intent === "form"
          ? "586px"
          : modalHeights[$height]};
  flex-direction: column;
  gap: ${({ $intent }) =>
    isTaskIntent($intent)
      ? vars.spacing.none
      : isActionIntent($intent) || $intent === "comparison"
        ? vars.spacing.lg
        : "0"};
  overflow: ${({ $intent }) =>
    isActionIntent($intent) ? "visible" : "hidden"};
  border-radius: ${({ $kind, $side, $intent, $drawerVariant }) =>
    $kind === "modal"
      ? $intent === "upload" || $intent === "confirmation"
        ? vars.radius.lg
        : vars.radius.md
      : $drawerVariant === "workspace"
        ? vars.radius.none
        : $side === "left"
          ? `0 ${vars.radius.md} ${vars.radius.md} 0`
          : `${vars.radius.md} 0 0 ${vars.radius.md}`};
  background: ${vars.color.background.surface};
  box-shadow: ${({ $kind, $intent }) =>
    $kind === "modal"
      ? $intent === "upload" || $intent === "confirmation"
        ? vars.shadow.compactDialog
        : isActionIntent($intent) || $intent === "comparison"
          ? vars.shadow.dropdown
          : vars.shadow.lg
      : vars.shadow.panel};
  color: ${vars.color.text.primary};
  pointer-events: auto;

  ${({ $intent }) =>
    ($intent === "selection" || $intent === "generation") &&
    css`
      max-height: calc(100dvh - ${vars.spacing.px56} * 2);
    `}

  ${({ $intent }) =>
    $intent === "aiContext" &&
    css`
      padding: ${vars.spacing.none};
      gap: ${vars.spacing.lg};
      overflow: visible;
      border-radius: ${vars.radius.md};
      box-shadow: ${vars.shadow.dropdown};
    `}

  &:focus {
    outline: none;
  }

  ${({ $kind, $drawerVariant }) =>
    $kind === "drawer" &&
    $drawerVariant === "workspace" &&
    css`
      height: 100%;
    `}
`;

const usesNewDesignHeader = (intent: ModalIntent) =>
  isActionIntent(intent) ||
  intent === "editor" ||
  intent === "comparison" ||
  intent === "aiContext";

const DialogHeader = styled.header<{
  $hasTitleLeading: boolean;
  $intent: ModalIntent;
  $workspace: boolean;
}>`
  box-sizing: border-box;
  display: flex;
  height: 56px;
  flex: 0 0 56px;
  align-items: center;
  justify-content: space-between;
  gap: ${vars.spacing.lg};
  padding: ${({ $hasTitleLeading, $intent }) =>
    usesNewDesignHeader($intent)
      ? `0 ${vars.spacing.sm} 0 ${
          $hasTitleLeading ? vars.spacing["2xl"] : vars.spacing.md
        }`
      : `${vars.spacing.sm} ${vars.spacing.lg}`};
  border-radius: inherit;
  border-bottom-right-radius: ${vars.radius.none};
  border-bottom-left-radius: ${vars.radius.none};
  border-bottom: ${({ $intent }) =>
      usesNewDesignHeader($intent)
        ? vars.borderWidth.medium
        : vars.borderWidth.thin}
    solid ${vars.color.border.subtle};
  background: ${vars.color.background.subtle};

  ${({ $intent }) =>
    ($intent === "selection" || $intent === "generation") &&
    css`
      padding-left: ${vars.spacing.lg};
    `}

  ${({ $intent }) =>
    $intent === "aiContext" &&
    css`
      padding: 0 ${vars.spacing.sm} 0 ${vars.spacing.lg};
    `}

  ${({ $workspace }) =>
    $workspace &&
    css`
      height: calc(${vars.spacing.px50} + ${vars.spacing.px20});
      flex-basis: calc(${vars.spacing.px50} + ${vars.spacing.px20});
      padding: ${vars.spacing.px20} ${vars.spacing.px20} 0;
      border-bottom: 0;
      background: ${vars.color.background.surface};
    `}
`;

const DialogTitleGroup = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: ${vars.spacing.sm};
`;

const DialogTitleLeading = styled.div`
  display: flex;
  width: 40px;
  height: 40px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
`;

const DialogTitle = styled.h2<{ $intent: ModalIntent }>`
  margin: 0;
  font-family: ${vars.typography.family.sans};
  font-size: ${({ $intent }) =>
    usesNewDesignHeader($intent)
      ? vars.typography.size.xl
      : vars.typography.size.lg};
  color: ${({ $intent }) =>
    $intent === "aiContext" ? vars.color.text.aiHeading : "inherit"};
  font-weight: ${vars.typography.weight.semibold};
  line-height: ${({ $intent }) =>
    usesNewDesignHeader($intent)
      ? vars.typography.lineHeight.xl
      : vars.typography.lineHeight.lg};
`;

const VisuallyHiddenTitle = styled.h2`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: calc(-1 * var(--inzeloop-ds-spacing-1px));
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const intentBodyPadding: Record<ModalIntent, string> = {
  standard: vars.spacing.lg,
  aiContext: `${vars.spacing.lg} ${vars.spacing.lg} calc(${vars.spacing.px50} + ${vars.spacing.xxs})`,
  action: `${vars.spacing.lg} ${vars.spacing.lg} 52px`,
  compactAction: `${vars.spacing.lg} ${vars.spacing.lg} 52px`,
  form: `${vars.spacing.lg} ${vars.spacing.lg} 52px`,
  selection: `${vars.spacing["2xl"]} ${vars.spacing["2xl"]} calc(${vars.spacing.px50} + ${vars.spacing.xxs})`,
  generation: `${vars.spacing["2xl"]} ${vars.spacing["2xl"]} calc(${vars.spacing.px50} + ${vars.spacing.xxs})`,
  confirmation: vars.spacing.xl,
  editor: vars.spacing.none,
  upload: vars.spacing.xl,
  comparison: `0 ${vars.spacing.lg} ${vars.spacing.lg}`,
};

export const DialogBody = styled.div<{
  $intent: ModalIntent;
  $kind: DialogKind;
  $drawerVariant: DrawerVariant;
  $hasHeader: boolean;
  $hasFooter: boolean;
}>`
  box-sizing: border-box;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: stretch;
  gap: ${({ $intent }) =>
    $intent === "comparison" || $intent === "generation"
      ? vars.spacing.lg
      : $intent === "upload"
        ? vars.spacing.lg
        : isActionIntent($intent)
          ? "10px"
          : "0"};
  min-height: 0;
  max-height: ${({ $intent }) =>
    isTaskIntent($intent) ? vars.layout.taskModalBodyMaxHeight : "none"};
  padding: ${({
    $intent,
    $kind,
    $drawerVariant,
  }: {
    $intent: ModalIntent;
    $kind: DialogKind;
    $drawerVariant: DrawerVariant;
  }) =>
    $kind === "drawer" && $drawerVariant === "workspace"
      ? `calc(${vars.spacing.lg} + ${vars.spacing.xs})`
      : intentBodyPadding[$intent]};
  overflow-y: auto;

  ${({ $intent }) =>
    $intent === "aiContext" &&
    css`
      @media (max-width: 480px) {
        padding-bottom: calc(${vars.spacing.px50} + ${vars.spacing.xl});
      }
    `}

  ${({ $kind, $drawerVariant, $hasHeader }) =>
    $kind === "drawer" &&
    $drawerVariant === "workspace" &&
    $hasHeader &&
    css`
      padding-top: 0;
    `}

  ${({ $kind, $drawerVariant, $hasFooter }) =>
    $kind === "drawer" &&
    $drawerVariant === "workspace" &&
    $hasFooter &&
    css`
      margin-bottom: ${vars.spacing.px20};
      padding-bottom: 0;
    `}
`;

export const DialogFooter = styled.footer<{
  $intent: ModalIntent;
  $workspace?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: ${({ $intent }) =>
    isActionIntent($intent) ? "center" : "flex-end"};
  gap: ${({ $intent }) =>
    isActionIntent($intent)
      ? "var(--inzeloop-ds-spacing-50px)"
      : vars.spacing.md};
  padding: ${({ $intent }) =>
    isActionIntent($intent)
      ? vars.spacing.lg
      : `${vars.spacing.md} ${vars.spacing.lg}`};
  border: ${({ $intent }) =>
    isActionIntent($intent)
      ? `${vars.borderWidth.hairline} solid ${vars.color.border.medium}`
      : "0"};
  border-top: ${({ $intent }) =>
    isActionIntent($intent)
      ? `${vars.borderWidth.hairline} solid ${vars.color.border.medium}`
      : `${vars.borderWidth.thin} solid ${vars.color.border.subtle}`};
  border-radius: ${({ $intent }) =>
    isActionIntent($intent) ? vars.radius.xl : vars.radius.none};
  background: ${vars.color.background.surface};
  box-shadow: ${({ $intent }) =>
    isActionIntent($intent) ? vars.shadow.sm : "none"};

  ${({ $intent }) =>
    $intent === "aiContext" &&
    css`
      box-sizing: border-box;
      position: absolute;
      left: 50%;
      bottom: 0;
      transform: translate(-50%, 50%);
      width: calc(
        ${vars.layout.aiContextModalWidth} - ${vars.spacing.px80} * 2
      );
      max-width: calc(100% - ${vars.spacing["2xl"]});
      padding: calc(${vars.spacing.lg} - ${vars.borderWidth.hairline});
      border: ${vars.borderWidth.hairline} solid ${vars.color.border.medium};
      border-radius: ${vars.radius.xl};
      box-shadow: ${vars.shadow.sm};
    `}

  ${({ $workspace }) =>
    $workspace &&
    css`
      min-height: calc(${vars.spacing.px50} + ${vars.spacing.xxs});
      flex-shrink: 0;
      margin: 0 ${vars.spacing.px20} ${vars.spacing.px20};
      padding: ${vars.spacing.px10} 0 0;
      border-top: ${vars.borderWidth.thin} solid ${vars.color.border.light};
      gap: ${vars.spacing.xl};
    `}

  ${({ $intent }) =>
    isActionIntent($intent) &&
    css`
      position: absolute;
      left: 50%;
      bottom: ${$intent === "form" ? "-35px" : "0"};
      z-index: 3;
      width: fit-content;
      transform: translate(-50%, 50%);
      white-space: nowrap;
    `}

  ${({ $intent }) =>
    isTaskIntent($intent) &&
    css`
      box-sizing: border-box;
      height: ${vars.spacing.px72};
      padding: calc(${vars.spacing.lg} - ${vars.borderWidth.thin})
        calc(${vars.spacing.lg} - ${vars.borderWidth.hairline});
    `}
`;

interface DialogProps extends DialogBaseProps {
  onEscape?: () => void;
  escapeFromOutside?: boolean;
  modal?: boolean;
  kind: DialogKind;
  size: DialogSize;
  side: DrawerSide;
  intent: ModalIntent;
  height: ModalHeight;
  drawerVariant: DrawerVariant;
}

const DialogHeaderActions = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: ${vars.spacing.xs};
`;

const Dialog = ({
  open,
  title,
  children,
  onClose,
  onEscape = onClose,
  escapeFromOutside = false,
  footer,
  closeLabel = "Close",
  closeOnEscape = true,
  closeOnOverlayClick = false,
  showCloseButton = true,
  descriptionId,
  className,
  titleLeading,
  headerActions,
  keepMounted = false,
  showHeader = true,
  surfaceRef: externalSurfaceRef,
  kind,
  size,
  side,
  intent,
  height,
  drawerVariant,
  modal = true,
}: DialogProps) => {
  const ownSurfaceRef = useRef<HTMLDivElement>(null);
  const surfaceRef = externalSurfaceRef ?? ownSurfaceRef;
  const generatedTitleId = useId();
  const titleId = `inzeloop-dialog-${generatedTitleId.replace(/:/g, "")}`;

  useFocusTrap(surfaceRef, open && modal);

  useEffect(() => {
    if (!open || !closeOnEscape) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const targetInside =
        event.target instanceof Node &&
        surfaceRef.current?.contains(event.target);
      const targetDialog =
        event.target instanceof Element &&
        event.target.closest('[role="dialog"]');
      if (
        event.key === "Escape" &&
        !event.defaultPrevented &&
        (modal || targetInside || (escapeFromOutside && !targetDialog))
      ) {
        event.preventDefault();
        onEscape();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [closeOnEscape, escapeFromOutside, modal, onEscape, open, surfaceRef]);

  if (!open && !keepMounted) return null;

  return (
    <DialogOverlay
      hidden={!open}
      style={!open ? { display: "none" } : undefined}
      $kind={kind}
      $modal={modal}
      $side={side}
      $intent={intent}
      $drawerVariant={drawerVariant}
      onClick={(event) => {
        if (
          modal &&
          closeOnOverlayClick &&
          event.target === event.currentTarget
        ) {
          onClose();
        }
      }}
    >
      <DialogSurface
        ref={surfaceRef}
        role="dialog"
        aria-modal={modal || undefined}
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
        className={className}
        data-dialog-intent={intent}
        data-drawer-variant={kind === "drawer" ? drawerVariant : undefined}
        data-escape-from-outside={
          open && escapeFromOutside ? "true" : undefined
        }
        $kind={kind}
        $size={size}
        $side={side}
        $intent={intent}
        $height={height}
        $drawerVariant={drawerVariant}
      >
        {showHeader ? (
          <DialogHeader
            $hasTitleLeading={Boolean(titleLeading)}
            $workspace={kind === "drawer" && drawerVariant === "workspace"}
            $intent={intent}
          >
            <DialogTitleGroup>
              {titleLeading && (
                <DialogTitleLeading>{titleLeading}</DialogTitleLeading>
              )}
              <DialogTitle id={titleId} $intent={intent}>
                {title}
              </DialogTitle>
            </DialogTitleGroup>
            <DialogHeaderActions>
              {headerActions}
              {showCloseButton && (
                <IconButton
                  aria-label={closeLabel}
                  onClick={onClose}
                  size={
                    intent === "aiContext" || isTaskIntent(intent)
                      ? "md"
                      : usesNewDesignHeader(intent)
                        ? "lg"
                        : "sm"
                  }
                >
                  <svg
                    aria-hidden="true"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M18 6L6 18M6 6L18 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </IconButton>
              )}
            </DialogHeaderActions>
          </DialogHeader>
        ) : (
          <VisuallyHiddenTitle id={titleId}>{title}</VisuallyHiddenTitle>
        )}
        <DialogBody
          $hasHeader={showHeader}
          $hasFooter={Boolean(footer)}
          $intent={intent}
          $kind={kind}
          $drawerVariant={drawerVariant}
        >
          {children}
        </DialogBody>
        {footer && (
          <DialogFooter
            $intent={intent}
            $workspace={kind === "drawer" && drawerVariant === "workspace"}
          >
            {footer}
          </DialogFooter>
        )}
      </DialogSurface>
    </DialogOverlay>
  );
};

export const Modal = ({
  size = "md",
  intent = "standard",
  height = "content",
  ...props
}: ModalProps) => (
  <Dialog
    {...props}
    kind="modal"
    size={size}
    side="right"
    intent={intent}
    height={height}
    drawerVariant="viewport"
  />
);

export const Drawer = ({
  side = "right",
  size = "md",
  variant = "viewport",
  ...props
}: DrawerProps) => (
  <Dialog
    {...props}
    kind="drawer"
    size={size}
    side={side}
    intent="standard"
    height="content"
    drawerVariant={variant}
  />
);
